import type {
  IGcfProductListProduct,
  IGcfProductListProductImage,
  IGcfProductListProductSize,
  IGcfProductListProductSizeVariation,
} from 'src/interfaces/Product';

type WithImages = { images?: IGcfProductListProductImage[] };

export const DEFAULT_SORT_ORDER = 999;

export const isSizeImageVisible = (
  size?: IGcfProductListProductSize,
): boolean => {
  if (!size?.variations) return false;

  const sorted = (
    Object.values(size.variations) as IGcfProductListProductSizeVariation[]
  )
    .slice()
    .sort(
      (a, b) =>
        (a.order ?? DEFAULT_SORT_ORDER) - (b.order ?? DEFAULT_SORT_ORDER),
    );

  return sorted.some((v) => v.config?.images?.some((img) => img.visible));
};

export const isProductImageVisible = (
  product?: IGcfProductListProduct,
): boolean => {
  if (!product?.sizes) return false;

  const defaultSize = product.config?.defaultSize;

  if (defaultSize !== undefined) {
    return isSizeImageVisible(product.sizes[defaultSize]);
  }

  return (Object.values(product.sizes) as IGcfProductListProductSize[]).some(
    (size) => isSizeImageVisible(size),
  );
};

/**
 * Set `visible: true` on the "first" flavor's images and `false` on all others.
 *
 * The caller decides which flavor counts as "first" by providing a predicate
 * (e.g. `order === 1` for the edit-component page, or `productCode === X`
 * for the override panel's first-checked-by-order strategy).
 */
export const updateFlavorImagesVisibility = <T extends WithImages>(
  flavors: T[],
  isFirstVisible: (flavor: T, index: number) => boolean,
): T[] =>
  flavors.map((flavor, i) => {
    const imageVisibility = isFirstVisible(flavor, i);

    if (!flavor.images?.length) {
      if (imageVisibility) {
        return { ...flavor, images: [{ visible: true }] };
      }

      return flavor;
    }

    return {
      ...flavor,
      images: flavor.images.map((img) => ({
        ...img,
        visible: imageVisibility,
      })),
    };
  });
